import {Component, EventEmitter, Input, OnInit, Output, OnDestroy, Renderer2} from '@angular/core';
import {PhotoData} from '../employee-badge-requests';
import * as EmployeeModels from '@employee-models/index';
import {IBadgeRequestPhotoInfo} from '@employee-models/data.models';
import {CommonConfig} from '@employee-configs/common.config';
import {BadgeRequestsService} from 'DBadgeEmployee-app/src/app/core/services/data-access/badge-requests.service';
import {debounceTime, map, take} from 'rxjs/operators';
import {ResponsiveService} from '@ui-app/services/responsive.service';
import {BaseComponent} from '@ui-app/components/base.component';
import {ApiConfig} from '@employee-configs/api.config';
import { Observable, fromEvent, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import {SpinnerService} from '@ui-app/components/spinner/spinner.service';
import {EmployeeBusinessService} from 'DBadgeEmployee-app/src/app/core/services/business.service';

@Component({
    selector: 'photo-compliance',
    templateUrl: './photo-compliance.component.html'
})
export class PhotoComplianceComponent extends BaseComponent implements OnInit, OnDestroy {

    @Input() userCanTakePhoto = true;
    @Input() canUseCurrentImage: boolean;
    @Input() photoData: PhotoData;
    @Input() userPhotoUrl: string = null;
    @Input() newFileName: string = null;
    @Input() requestData: EmployeeModels.DM.IBadgeRequestInfoEmployee;
    @Input() isVisitorRequest = false;

    @Output() onUpload = new EventEmitter<IBadgeRequestPhotoInfo>();
    @Output() onUseCurrentPhoto = new EventEmitter<IBadgeRequestPhotoInfo>();
    @Output() onDelete = new EventEmitter<void>();

    readonly uploadConfig = {
        ...CommonConfig.fileUploadRestrictions.photo,
        areaTitle: 'Drag & Drop photo here or',
        customRestrictions: 'Provide a photo that meets all mentioned requirements',
        errors: {
            filesCount: 'Only one file can be uploaded',
        },
    };
    readonly cameraConfig = CommonConfig.cameraConfig;
    isDesktop: boolean;
    resizeSubscription: Subscription;

    constructor(
        private badgeRequestsService: BadgeRequestsService,
        private responsiveService: ResponsiveService,
        private toastr: ToastrService,
         private spinnerService: SpinnerService,
        private renderer: Renderer2,
        private businessService: EmployeeBusinessService
    ) {
        super();
    }

    ngOnInit(): void {
        this.responsiveService.isDesktop.pipe(take(1)).subscribe(data => this.isDesktop = data);
        this.checkResolutionAndShowToast();
        this.resizeSubscription = fromEvent(window, 'resize')
        .pipe(debounceTime(800)) 
        .subscribe(() => this.checkResolutionAndShowToast());
    }

    ngOnDestroy(): void {
        if (this.resizeSubscription) {
            this.resizeSubscription.unsubscribe();
        }
    }

    checkResolutionAndShowToast(): void {
        if (window.matchMedia('(max-width: 767.9px)').matches) {
            this.renderer.addClass(document.body, 'mobile-view');
            this.toastr.info('Use desktop view for better experience', 'Info:');
        } else {
            this.renderer.removeClass(document.body, 'mobile-view');
        }
    }

    onPhotoTaken(event: PhotoData): void {
        this.photoData = event;
        if (this.photoData.url) {
            this.userPhotoUrl = this.photoData.url;
        }

        if (!!this.photoData.uId) {
            if (this.photoData.uId && !this.photoData.url) {
                this.userPhotoUrl = `${ApiConfig.File.fileUrl}/${this.photoData.uId}`;
            }
            this.photoData.url = this.userPhotoUrl;
            const file = this.getFileFromPhotoData(this.photoData);
            this.onUpload.emit(file);
        }
    }

    deletePhoto(): void {
        this.photoData = null;
        this.userPhotoUrl = '';
        this.onDelete.emit();
    }

    useCurrentPhoto(): void {
        const userUid = this.requestData.lastApprovedBadgeImageUid;
        this.photoData = {
            uId: userUid
        };
        console.log("useCurrentPhoto");
        console.log(userUid);
        let request: Observable<EmployeeModels.DM.IDownloadUrlResponse>;
        if (this.isVisitorRequest) {
            request = this.badgeRequestsService.getVisitorDownloadUrl(userUid);
        } else {
            request = this.badgeRequestsService.getDownloadUrl(userUid);
        }

        this.subs = request.pipe(
            map(result => {
                this.userPhotoUrl = result.url;
                const currentPhotoData = { uId: userUid, ...result };
                return this.getFileFromPhotoData(currentPhotoData);
            })
        ).subscribe(file => this.onUseCurrentPhoto.emit(file));
    }

 onPhotoUploaded(event: PhotoData): void {
    this.spinnerService.next(true);
    this.photoData = event;

    // Check photo quality
    const image = new Image();
     
    image.crossOrigin = "Anonymous";
    image.src = this.photoData.url;

    image.onload = () => {
        const currentUser = this.businessService.userProfileService.user;
        if (!currentUser.isUSI) {
            // Check resolution
            if (
            !(
                (image.naturalWidth >= 800 && image.naturalHeight >= 600) ||
                (image.naturalHeight >= 800 && image.naturalWidth >= 600)
            )
        ) {
            this.toastr.error(
                'The photo resolution is too low. Please upload a photo with at least 800x800 resolution.',
                'Error:'
            );
            this.spinnerService.next(false);
            this.deletePhoto();
            return;
        }

        // 2. Black and white check (improved)
        const colorfulness = this.checkColorfulness(image);
        if (colorfulness < 15) { // Adjust threshold as needed
            this.toastr.error(
                'The photo appears to be black and white. Please upload a color photo.',
                'Error:'
            );
            this.spinnerService.next(false);
            this.deletePhoto();
            return;
        }

        // 3. Black background check (strict or threshold)
        const blackSamples = this.sampleBlackness(image);
        const blackPixelPercentage = blackSamples.reduce((a, b) => a + b, 0) / blackSamples.length;

        if (blackPixelPercentage > 50) { // Adjust threshold as needed
            this.toastr.error(
                'The photo background is too black. Please upload a photo with a lighter background.',
                'Error:'
            );
            this.spinnerService.next(false);
            this.deletePhoto();
            return;
        }
        this.spinnerService.next(false);
    }
        // If photo quality is acceptable, proceed with upload
        const file = this.getFileFromPhotoData(this.photoData);
        this.onUpload.emit(file);
    };
}
private sampleBlackness(img: HTMLImageElement): number[] {
    const blackSamples: number[] = [];
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const regionSize = 12;
    const halfRegion = Math.floor(regionSize / 2);

    const samplePositions = [
        { x: 0, y: 0 },                         // top-left corner
        { x: w - 1, y: 0 },                     // top-right corner
        { x: 0, y: h - 1 },                     // bottom-left corner
        { x: w - 1, y: h - 1 },                 // bottom-right corner
        { x: Math.floor(w / 2), y: 0 },         // top edge center
        { x: Math.floor(w / 2), y: h - 1 },     // bottom edge center
        { x: 0, y: Math.floor(h / 2) },         // left edge center
        { x: w - 1, y: Math.floor(h / 2) }      // right edge center
    ];

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return [];
    ctx.drawImage(img, 0, 0, w, h);

    samplePositions.forEach(pos => {
        let blackCount = 0;
        let count = 0;
        for (let dx = -halfRegion; dx < regionSize - halfRegion; dx++) {
            for (let dy = -halfRegion; dy < regionSize - halfRegion; dy++) {
                const x = Math.min(Math.max(pos.x + dx, 0), w - 1);
                const y = Math.min(Math.max(pos.y + dy, 0), h - 1);
                const pixel = ctx.getImageData(x, y, 1, 1).data;
                // Use one of the following:
                // Threshold-based black:
                if (this.isBlackPixel(pixel[0], pixel[1], pixel[2])) {
                    blackCount++;
                }
                count++;
            }
        }
        blackSamples.push((blackCount / count) * 100);
    });

    return blackSamples;
}

// Code Generated by Sidekick is for learning and experimentation purposes only.
// Strict: Only pure black pixels (rare in real images)
private isPureBlackPixel(r: number, g: number, b: number): boolean {
    return r === 0 && g === 0 && b === 0;
}

// Code Generated by Sidekick is for learning and experimentation purposes only.
// Threshold: Pixels that are visually black
private isBlackPixel(r: number, g: number, b: number, threshold = 30): boolean {
    return r < threshold && g < threshold && b < threshold;
}

// Code Generated by Sidekick is for learning and experimentation purposes only.
private calculateBrightness(r: number, g: number, b: number): number {
    return (r * 299 + g * 587 + b * 114) / 1000;
}

// Code Generated by Sidekick is for learning and experimentation purposes only.
private checkColorfulness(img: HTMLImageElement): number {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return 0;
    ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);

    const imageData = ctx.getImageData(0, 0, img.naturalWidth, img.naturalHeight).data;
    const rg = [], yb = [];
    for (let i = 0; i < imageData.length; i += 4) {
        const r = imageData[i], g = imageData[i + 1], b = imageData[i + 2];
        rg.push(Math.abs(r - g));
        yb.push(Math.abs(0.5 * (r + g) - b));
    } 
    const meanRG = rg.reduce((a, b) => a + b, 0) / rg.length;
    const meanYB = yb.reduce((a, b) => a + b, 0) / yb.length;
    const stdRG = Math.sqrt(rg.reduce((a, b) => a + Math.pow(b - meanRG, 2), 0) / rg.length);
    const stdYB = Math.sqrt(yb.reduce((a, b) => a + Math.pow(b - meanYB, 2), 0) / yb.length);
    // Colorfulness metric
    return Math.sqrt(stdRG ** 2 + stdYB ** 2) + 0.3 * Math.sqrt(meanRG ** 2 + meanYB ** 2);
}

    getFileFromPhotoData(photoData: PhotoData): IBadgeRequestPhotoInfo {
        const { uId, contentType, length, url } = photoData;
        return {
            uId,
            name: this.newFileName,
            contentType,
            length,
            url,
            documentType: EmployeeModels.DE.DocumentTypeEnum.FrontFace
        };
    }
}